import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { withAdminUnlock, adminDeleteListing } from "@/lib/admin";
import { useAdminPassword } from "@/components/admin/AdminPassword";
import { EditListingDialog } from "@/components/admin/EditListingDialog";

const errMsg = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export const AdminListingBar = ({ item, onUpdated }) => {
  const navigate = useNavigate();
  const [askPassword, passwordDialog] = useAdminPassword();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const openEdit = () => setEditOpen(true);

  const remove = async () => {
    setBusy(true);
    setConfirmOpen(false);
    try {
      await withAdminUnlock(() => adminDeleteListing(item.id), askPassword);
      toast.success("Listing deleted");
      navigate("/", { replace: true });
    } catch (err) {
      if (err.message !== "cancelled") toast.error(errMsg(err));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div data-testid="admin-listing-bar" className="flex flex-wrap items-center gap-3 border border-[#C5A059] bg-[#0F0F0F] text-white px-4 py-3">
      <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mr-auto"><ShieldCheck className="h-4 w-4" strokeWidth={1.5} /> Admin</span>
      <button data-testid="admin-edit-listing-button" onClick={openEdit} className="flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.25em] uppercase border border-white/30 hover:border-[#C5A059] hover:text-[#C5A059]">
        <Pencil className="h-3.5 w-3.5" /> Edit
      </button>
      <button data-testid="admin-delete-listing-button" onClick={() => setConfirmOpen(true)} className="flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.25em] uppercase border border-red-400/60 text-red-300 hover:bg-red-500 hover:text-white">
        <Trash2 className="h-3.5 w-3.5" /> Delete
      </button>

      <EditListingDialog item={item} open={editOpen} onOpenChange={setEditOpen} onUpdated={onUpdated} askPassword={askPassword} />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="admin-delete-dialog" className="rounded-none bg-[#FBF9F5]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete this listing?</AlertDialogTitle>
            <AlertDialogDescription>“{item.title}” will be removed from Lamka Online for everyone, including the mobile app. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); remove(); }} className="rounded-none bg-red-600 hover:bg-red-700">
              {busy ? "Deleting…" : "Delete listing"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {passwordDialog}
    </div>
  );
};
